import { normalizeDigits, PERSIAN_DECIMAL_SEPARATOR, toPersianDigits } from './digits.js';

/**
 * Rates (AC §4.2): decimal strings with six fractional digits, for example `"0.080000"` for 8%.
 * They are displayed as a percentage by moving the decimal point on the string, so no
 * floating-point arithmetic touches the value and nothing is rounded.
 */

const RATE_PATTERN = /^(\d+)(?:\.(\d+))?$/;

export interface RateFormatOptions {
  /**
   * What a value that is not a decimal rate does: `throw` (default) raises a `RangeError`; `raw`
   * returns the value unchanged, for read-only screens that must never fail on one field.
   */
  readonly invalid?: 'throw' | 'raw';
}

/** `"0.080000"` → `۸٪`, `"0.025000"` → `۲٫۵٪`, `"0.123456"` → `۱۲٫۳۴۵۶٪`. */
export function formatRatePercent(rate: string, options: RateFormatOptions = {}): string {
  const match = RATE_PATTERN.exec(rate.trim());
  if (match === null) {
    if (options.invalid === 'raw') {
      return rate;
    }
    throw new RangeError(`Not a decimal rate: ${rate}`);
  }
  const whole = match[1] ?? '0';
  const fraction = (match[2] ?? '').padEnd(2, '0');
  // Percent = rate × 100: the first two fractional digits move into the whole part.
  const percentWhole = `${whole}${fraction.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const percentFraction = fraction.slice(2).replace(/0+$/, '');
  const decimal =
    percentFraction === '' ? '' : `${PERSIAN_DECIMAL_SEPARATOR}${toPersianDigits(percentFraction)}`;

  return `${toPersianDigits(percentWhole)}${decimal}٪`;
}

/**
 * A percentage typed by a person (`۸`, `8.5`, `۱۲٫۲۵`; Persian, Arabic-Indic, or Latin digits) as the
 * API rate string with six fractional digits (AC §4.2): `8.5` → `"0.085000"`. Only string shifting is
 * used, never floating point. `null` when the text is not a percentage in `[0, 100)` with at most four
 * decimals (six on the rate).
 */
export function parseRatePercentInput(input: string): string | null {
  const text = normalizeDigits(input).trim().replace(/[٫,]/g, '.').replace(/[%٪]$/, '').trim();
  const match = /^(\d{1,2})(?:\.(\d{0,4}))?$/.exec(text);
  if (match === null) {
    return null;
  }
  const whole = (match[1] ?? '0').padStart(2, '0');
  const fraction = (match[2] ?? '').padEnd(4, '0');
  // percent ab.cdef → rate 0.abcdef
  return `0.${whole}${fraction}`;
}

/** The input text of an existing rate: `"0.085000"` → `8.5` (Latin digits, for an LTR field). */
export function rateToPercentInput(rate: string): string {
  const match = RATE_PATTERN.exec(rate.trim());
  if (match === null) {
    return '';
  }
  const fraction = (match[2] ?? '').padEnd(2, '0');
  const whole = `${match[1] ?? '0'}${fraction.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const rest = fraction.slice(2).replace(/0+$/, '');
  return rest === '' ? whole : `${whole}.${rest}`;
}
